import { useState, useEffect } from "react";
import { Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuizzes } from "@/hooks/useQuizzes";
import { useScoringPreview } from "@/hooks/useScoringPreview";

export function DashboardScoringWidget() {
  const { data: quizzes, isLoading: quizzesLoading } = useQuizzes();
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null);

  // Auto-select first quiz
  useEffect(() => {
    if (quizzes?.length && !selectedQuizId) {
      setSelectedQuizId(quizzes[0].quizId);
    }
  }, [quizzes, selectedQuizId]);

  const { data, isLoading, isError } = useScoringPreview(selectedQuizId);

  if (quizzesLoading) {
    return <DashboardScoringWidgetSkeleton />;
  }

  if (!quizzes?.length) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center gap-2 space-y-0">
          <Target className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Scoring-Übersicht</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Erstellen Sie Ihr erstes Quiz, um die Scoring-Übersicht zu sehen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Scoring-Übersicht</CardTitle>
        </div>
        <select
          value={selectedQuizId ?? ""}
          onChange={(e) => setSelectedQuizId(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          {quizzes.map((q) => (
            <option key={q.quizId} value={q.quizId}>
              {q.title}
            </option>
          ))}
        </select>
      </CardHeader>

      <CardContent className="space-y-6">
        {isLoading ? (
          <ScoringContentSkeleton />
        ) : isError ? (
          <p className="text-sm text-destructive">
            Scoring-Daten konnten nicht geladen werden.
          </p>
        ) : data && data.summary.totalRules === 0 ? (
          <p className="text-sm text-muted-foreground">
            Keine Scoring-Regeln vorhanden. Erstellen Sie Scoring-Regeln für
            dieses Quiz.
          </p>
        ) : data ? (
          <>
            {/* Rules */}
            <div>
              <h3 className="mb-2 text-sm font-medium">
                Regeln ({data.summary.totalRules} aktiv)
              </h3>
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {data.rules.map((rule) => (
                  <div
                    key={rule.ruleId}
                    className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
                  >
                    <span className="truncate">
                      {rule.name}
                      <span className="text-muted-foreground">
                        {" "}
                        → {rule.targetTag} +{rule.weight}
                      </span>
                    </span>
                    {!rule.matchesQuiz && (
                      <span className="ml-2 shrink-0 text-xs text-destructive">
                        Frage fehlt
                      </span>
                    )}
                  </div>
                ))}
                {data.rules.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Keine Regeln definiert.
                  </p>
                )}
              </div>
            </div>

            {/* Products Table */}
            <div>
              <h3 className="mb-2 text-sm font-medium">
                Top-Produkte nach potenziellem Score
              </h3>
              {data.products.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">Produkt</th>
                      <th className="pb-2 font-medium">Tags</th>
                      <th className="pb-2 text-right font-medium">
                        Max. Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.products.map((product) => (
                      <tr
                        key={product.productId}
                        className="border-b last:border-0"
                      >
                        <td className="py-1.5 truncate max-w-[200px]">
                          {product.name}
                        </td>
                        <td className="py-1.5">
                          {product.matchedTags.length}{" "}
                          {product.matchedTags.length === 1 ? "Tag" : "Tags"}
                        </td>
                        <td className="py-1.5 text-right">
                          {product.score} Punkte
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Keine Produkte vorhanden.
                </p>
              )}
            </div>

            {/* Summary */}
            <p className="text-xs text-muted-foreground">
              {data.summary.totalRules} Regeln · {data.summary.totalProducts}{" "}
              Produkte · {data.summary.productsWithTags} getaggt
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function DashboardScoringWidgetSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 space-y-0">
        <Target className="h-5 w-5 text-muted-foreground" />
        <div className="h-6 w-40 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <ScoringContentSkeleton />
      </CardContent>
    </Card>
  );
}

function ScoringContentSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-4 w-24 animate-pulse rounded bg-muted" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-8 animate-pulse rounded bg-muted" />
      ))}
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-6 animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}
